import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { signupLeadsApi } from '@/services/signupLeads.service';
import type { SignupLeadItem, SignupLeadNudgeResult } from '@/types/signupLead.types';

export const SIGNUP_LEADS_QUERY_KEY = 'signup-leads';

interface UseSignupLeadsTabParams {
  enabled: boolean;
  search: string;
  campusId: string;
  page: number;
  limit: number;
}

export interface UseSignupLeadsTabResult {
  leads: SignupLeadItem[];
  total: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  selectedIds: string[];
  isNudging: boolean;
  dismissingId: string | null;
  /** Inline feedback — this panel has no toast system, matching the rest of the app. */
  nudgeResult: SignupLeadNudgeResult | null;
  nudgeError: string | null;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onClearSelection: () => void;
  onNudgeSelected: () => void;
  onNudgeOne: (id: string) => void;
  onDismiss: (id: string) => void;
}

/**
 * The "Didn't Finish Signup" tab.
 *
 * Split out of `useUsersListPage` because these rows are signup leads, not users — a different
 * collection with different actions — even though they share the page's toolbar and pagination.
 */
export const useSignupLeadsTab = (params: UseSignupLeadsTabParams): UseSignupLeadsTabResult => {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [nudgeResult, setNudgeResult] = useState<SignupLeadNudgeResult | null>(null);
  const [nudgeError, setNudgeError] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(params.search, 300);

  const listQuery = useQuery({
    queryKey: [
      SIGNUP_LEADS_QUERY_KEY,
      { search: debouncedSearch, campusId: params.campusId, page: params.page },
    ],
    queryFn: () =>
      signupLeadsApi.list({
        // The queue only ever shows people still stuck — converted and dismissed leads are done.
        status: 'pending',
        search: debouncedSearch || undefined,
        campusId: params.campusId || undefined,
        sort: 'newest',
        page: params.page,
        limit: params.limit,
      }),
    enabled: params.enabled,
    placeholderData: (previous) => previous,
  });

  const leads = listQuery.data?.items ?? [];

  const nudgeMutation = useMutation({
    mutationFn: (ids: string[]) => signupLeadsApi.nudge(ids),
    onSuccess: (result) => {
      setNudgeResult(result);
      setNudgeError(null);
      setSelectedIds([]);
      void queryClient.invalidateQueries({ queryKey: [SIGNUP_LEADS_QUERY_KEY] });
    },
    onError: (error: unknown) => {
      setNudgeResult(null);
      setNudgeError(error instanceof Error ? error.message : 'Could not send the nudge.');
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => signupLeadsApi.dismiss(id),
    onMutate: (id) => setDismissingId(id),
    onError: (error: unknown) => {
      setNudgeError(error instanceof Error ? error.message : 'Could not dismiss that lead.');
    },
    onSettled: () => {
      setDismissingId(null);
      void queryClient.invalidateQueries({ queryKey: [SIGNUP_LEADS_QUERY_KEY] });
    },
  });

  const clearFeedback = (): void => {
    setNudgeResult(null);
    setNudgeError(null);
  };

  return {
    leads,
    total: listQuery.data?.total ?? 0,
    page: listQuery.data?.page ?? params.page,
    totalPages: listQuery.data?.totalPages ?? 1,
    isLoading: listQuery.isLoading && listQuery.fetchStatus !== 'idle',
    isError: listQuery.isError,
    errorMessage: listQuery.error instanceof Error ? listQuery.error.message : null,
    selectedIds,
    isNudging: nudgeMutation.isPending,
    dismissingId,
    nudgeResult,
    nudgeError,
    onToggleSelect: (id) => {
      clearFeedback();
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
      );
    },
    onToggleSelectAll: () => {
      clearFeedback();
      // Only selects what is on screen — "select all" across unloaded pages would be a
      // surprising amount of email to send from one click.
      setSelectedIds((prev) => (prev.length === leads.length ? [] : leads.map((lead) => lead.id)));
    },
    onClearSelection: () => setSelectedIds([]),
    onNudgeSelected: () => {
      clearFeedback();
      if (selectedIds.length > 0) nudgeMutation.mutate(selectedIds);
    },
    onNudgeOne: (id) => {
      clearFeedback();
      nudgeMutation.mutate([id]);
    },
    onDismiss: (id) => {
      clearFeedback();
      dismissMutation.mutate(id);
    },
  };
};
